// filepath: apps/web/src/features/admin/templates/update/ConflictDiffPanel.tsx
//
// FR-UI-ADM-12 AC-2 — side-by-side diff renderer (installed vs
// update). Pure SPA from the cached ConflictReport payload; no
// extra fetch.
//
// Highlight contract (FR-UI-CORE-9):
//   - changed fields: yellow background (#FFF8DC)
//   - removed fields: red background (#FCE4E4)
//   - added fields: green background (#E6FFE6)
//
// INV-9: the renderer uses text nodes only — no
// `dangerouslySetInnerHTML`. Field names / values are rendered
// verbatim.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { DefinitionDiff } from "./types";

interface ConflictDiffPanelProps {
  diffs: DefinitionDiff[];
}

export function ConflictDiffPanel({
  diffs,
}: ConflictDiffPanelProps): React.ReactElement {
  const { t } = useTranslation();

  if (diffs.length === 0) {
    return (
      <div
        className="rounded border border-border bg-background p-6 text-center text-sm text-muted-foreground"
        data-testid="template-update-diff-empty"
      >
        {t("admin.templates.update.list.empty")}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="template-update-diff-panel">
      {diffs.map((d) => (
        <div
          key={`${d.type}@${d.id}`}
          className="overflow-x-auto rounded border border-border bg-background"
          data-testid="template-update-diff-row"
          data-diff-type={d.type}
          data-diff-id={d.id}
        >
          <div className="border-b border-border bg-muted px-3 py-2 text-xs font-mono">
            {d.type} · {d.id} · v{d.base_version} → v{d.target_version}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2">Field</th>
                <th className="px-3 py-2">Installed</th>
                <th className="px-3 py-2">Target</th>
              </tr>
            </thead>
            <tbody>
              {(d.field_classifications ?? []).map((fc) => (
                <tr
                  key={`${fc.entity_type}.${fc.field_name}`}
                  data-classification={fc.classification}
                  className={
                    fc.classification === "modified_by_tenant"
                      ? "border-b border-border bg-yellow-50"
                      : fc.classification === "added_by_tenant"
                        ? "border-b border-border bg-green-50"
                        : "border-b border-border"
                  }
                  data-testid="template-update-diff-cell"
                >
                  <td className="px-3 py-2 font-mono text-xs">
                    {fc.entity_type}.{fc.field_name}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {fc.classification === "added_by_tenant" ? "—" : "value"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {fc.classification === "unchanged_by_tenant"
                      ? "value"
                      : fc.classification === "modified_by_tenant"
                        ? "value (changed)"
                        : "value (new)"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
